import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Download, BarChart3, Users, Baby, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from "date-fns";
import { ar } from "date-fns/locale";

const months = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

export default function AttendanceReports() {
  const { profile } = useAuth();
  const nurseryId = profile?.nursery_id;

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth().toString());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear().toString());
  const [viewType, setViewType] = useState<"children" | "staff">("children");

  const monthStart = startOfMonth(new Date(parseInt(selectedYear), parseInt(selectedMonth)));
  const monthEnd = endOfMonth(monthStart);
  const startStr = format(monthStart, "yyyy-MM-dd");
  const endStr = format(monthEnd, "yyyy-MM-dd");

  const { data: attendance, isLoading: loadingAtt } = useQuery({
    queryKey: ["attendance-report", nurseryId, startStr, endStr, viewType],
    queryFn: async () => {
      const { data } = await supabase
        .from("attendance")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .eq("attendee_type", viewType === "children" ? "child" : "staff")
        .gte("date", startStr)
        .lte("date", endStr);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: people, isLoading: loadingPeople } = useQuery({
    queryKey: ["attendance-people", nurseryId, viewType],
    queryFn: async () => {
      if (viewType === "children") {
        const { data } = await supabase.from("children").select("id, full_name, classes(name)").eq("nursery_id", nurseryId!).order("full_name");
        return data || [];
      } else {
        const { data } = await supabase.from("staff").select("id, full_name, role").eq("nursery_id", nurseryId!).order("full_name");
        return data || [];
      }
    },
    enabled: !!nurseryId,
  });

  const workDays = useMemo(() => {
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd > now ? now : monthEnd });
    return days.filter((d) => !isWeekend(d)).length;
  }, [selectedMonth, selectedYear]);

  const stats = useMemo(() => {
    if (!attendance || !people) return [];
    return people.map((p: any) => {
      const records = attendance.filter((a: any) => a.attendee_id === p.id);
      const present = records.filter((r: any) => r.status === "present").length;
      const absent = records.filter((r: any) => r.status === "absent").length;
      const late = records.filter((r: any) => r.status === "late").length;
      const total = present + absent + late;
      const rate = total > 0 ? Math.round(((present + late) / total) * 100) : 0;
      return {
        id: p.id,
        name: p.full_name,
        extra: viewType === "children" ? p.classes?.name : p.role,
        present,
        absent,
        late,
        rate,
      };
    });
  }, [attendance, people, viewType]);

  const overallStats = useMemo(() => {
    const totalPresent = stats.reduce((s, p) => s + p.present, 0);
    const totalAbsent = stats.reduce((s, p) => s + p.absent, 0);
    const totalLate = stats.reduce((s, p) => s + p.late, 0);
    const total = totalPresent + totalAbsent + totalLate;
    const rate = total > 0 ? Math.round(((totalPresent + totalLate) / total) * 100) : 0;
    return { totalPresent, totalAbsent, totalLate, rate, total };
  }, [stats]);

  const isLoading = loadingAtt || loadingPeople;
  const roleLabels: Record<string, string> = {
    teacher: "مدرس/ة", supervisor: "مشرف/ة", admin: "إداري/ة", driver: "سائق", cleaner: "عامل نظافة",
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={viewType} onValueChange={(v: any) => setViewType(v)}>
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="children">الأطفال</SelectItem>
            <SelectItem value="staff">الموظفين</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((m, i) => (
              <SelectItem key={i} value={i.toString()}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "نسبة الحضور", value: `${overallStats.rate}%`, icon: TrendingUp, color: "text-primary" },
          { label: "إجمالي حضور", value: overallStats.totalPresent, icon: viewType === "children" ? Baby : Users, color: "text-primary" },
          { label: "إجمالي غياب", value: overallStats.totalAbsent, icon: viewType === "children" ? Baby : Users, color: "text-destructive" },
          { label: "إجمالي تأخر", value: overallStats.totalLate, icon: viewType === "children" ? Baby : Users, color: "text-yellow-600" },
        ].map((s, i) => (
          <Card key={i} className="shadow-card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <div>
                <div className={`text-lg font-bold ${s.color}`}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : s.value}
                </div>
                <div className="text-[11px] text-muted-foreground">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detail Table */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">
            تقرير حضور {viewType === "children" ? "الأطفال" : "الموظفين"} - {months[parseInt(selectedMonth)]} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : stats.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">لا توجد بيانات</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-right p-3 font-medium text-muted-foreground">#</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">الاسم</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">
                      {viewType === "children" ? "الفصل" : "الوظيفة"}
                    </th>
                    <th className="text-right p-3 font-medium text-muted-foreground">حاضر</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">غائب</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">متأخر</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">نسبة الحضور</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s, i) => (
                    <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-muted-foreground">{i + 1}</td>
                      <td className="p-3 font-medium text-foreground">{s.name}</td>
                      <td className="p-3 text-muted-foreground text-xs">
                        {viewType === "staff" ? roleLabels[s.extra] || s.extra : s.extra || "-"}
                      </td>
                      <td className="p-3 text-primary font-medium">{s.present}</td>
                      <td className="p-3 text-destructive font-medium">{s.absent}</td>
                      <td className="p-3 text-yellow-600 font-medium">{s.late}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary transition-all"
                              style={{ width: `${s.rate}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-foreground">{s.rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
